import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip
} from '@mui/material';
import { Search, Edit as EditIcon, Add, Delete as DeleteIcon, OpenInNew, Check, Close } from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, addDays, startOfDay, differenceInCalendarDays } from 'date-fns';
import { parseTimeEntryDate, formatTimeEntry, toDatetimeLocalForEntry, parseLocalDate } from '../utils/dateUtils';
import { timeService } from '../services/timeService';
import { workFunctionService } from '../services/workFunctionService';
import { employeeService } from '../services/employeeService';
import { useAuth } from '../hooks/useAuth';

const WEEK_STARTS_SUNDAY = 0;
const OT_THRESHOLD_HOURS = 40;

/** Biweekly pay periods (14 calendar days), anchored to this Sunday. Adjust if your payroll uses a different start date. */
const PAY_PERIOD_ANCHOR = new Date(2024, 0, 7); // Jan 7, 2024 — Sunday
const PAY_PERIOD_LENGTH_DAYS = 14;

/** @param {number} periodOffsetFromCurrent 0 = period containing today, -1 = previous */
function getPayPeriodRange(periodOffsetFromCurrent) {
  const today = startOfDay(new Date());
  const anchor = startOfDay(PAY_PERIOD_ANCHOR);
  const diffDays = differenceInCalendarDays(today, anchor);
  let periodIndex = Math.floor(diffDays / PAY_PERIOD_LENGTH_DAYS);
  periodIndex += periodOffsetFromCurrent;
  const start = addDays(anchor, periodIndex * PAY_PERIOD_LENGTH_DAYS);
  const end = addDays(start, PAY_PERIOD_LENGTH_DAYS - 1);
  return { start, end };
}

/** Sun–Sat week containing the given calendar day (local). */
function getSundayWeekRangeContaining(date) {
  const d = startOfDay(date);
  return {
    start: startOfWeek(d, { weekStartsOn: WEEK_STARTS_SUNDAY }),
    end: endOfWeek(d, { weekStartsOn: WEEK_STARTS_SUNDAY })
  };
}

/** Sun–Sat week immediately before the start of the current pay period. */
function getWeekBeforeCurrentPayPeriod() {
  const { start: payStart } = getPayPeriodRange(0);
  return getSundayWeekRangeContaining(addDays(payStart, -1));
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WF_OTHER = '__other__';
const toYMD = (d) => (d ? format(d, 'yyyy-MM-dd') : '');
const toDatetimeLocal = (v) => toDatetimeLocalForEntry(v);

/** Group entries into timecards: one per (employee, week). */
function buildTimecards(entries) {
  const byKey = new Map();
  for (const e of entries) {
    if (!e.clock_in) continue;
    const d = parseTimeEntryDate(e.clock_in) || new Date(e.clock_in);
    const weekStart = startOfWeek(d, { weekStartsOn: WEEK_STARTS_SUNDAY });
    const key = `${e.employee_id}::${weekStart.getTime()}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        employeeId: e.employee_id,
        employeeNumber: e.employee_number,
        employeeName: [e.employee_first_name, e.employee_last_name].filter(Boolean).join(' ') || '—',
        weekStart,
        weekEnd: endOfWeek(d, { weekStartsOn: WEEK_STARTS_SUNDAY }),
        entries: []
      });
    }
    byKey.get(key).entries.push(e);
  }
  const cards = Array.from(byKey.values());
  cards.forEach((tc) => {
    const total = tc.entries.reduce((s, e) => s + (parseFloat(e.hours_worked) || 0), 0);
    tc.totalHours = total;
    tc.straightHours = Math.min(OT_THRESHOLD_HOURS, total);
    tc.otHours = Math.max(0, total - OT_THRESHOLD_HOURS);
  });
  return cards.sort((a, b) => b.weekStart - a.weekStart || (a.employeeName || '').localeCompare(b.employeeName || ''));
}

const Timecards = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [dateFrom, setDateFrom] = useState(() =>
    toYMD(getSundayWeekRangeContaining(new Date()).start)
  );
  const [dateTo, setDateTo] = useState(() => toYMD(getSundayWeekRangeContaining(new Date()).end));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resultsEmployeeFilterId, setResultsEmployeeFilterId] = useState(null);
  const [weekViewOpen, setWeekViewOpen] = useState(false);
  const [weekViewLoading, setWeekViewLoading] = useState(false);
  const [weekViewData, setWeekViewData] = useState(null);
  const [pendingPunches, setPendingPunches] = useState([]);
  const [savingNewPunches, setSavingNewPunches] = useState(false);
  const [savingPendingId, setSavingPendingId] = useState(null);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [inlineEditForm, setInlineEditForm] = useState({
    clockIn: '',
    clockOut: '',
    workOrderNumber: '',
    workFunctionId: '',
    workFunctionOther: ''
  });
  const [workFunctions, setWorkFunctions] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({
    clockIn: '',
    clockOut: '',
    hoursWorked: '',
    notes: '',
    workOrderNumber: '',
    workFunctionId: '',
    workFunctionOther: '',
    auditNotes: ''
  });
  const [editSaving, setEditSaving] = useState(false);
  const [createTimecardOpen, setCreateTimecardOpen] = useState(false);
  const [createTimecardEmployeeId, setCreateTimecardEmployeeId] = useState('');
  const [createTimecardWeekDate, setCreateTimecardWeekDate] = useState(() => toYMD(startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_SUNDAY })));
  const [employeesForCreate, setEmployeesForCreate] = useState([]);
  const [createTimecardLoading, setCreateTimecardLoading] = useState(false);

  const fullName = (e) =>
    [e?.employee_first_name, e?.employee_last_name].filter(Boolean).join(' ') ||
    [e?.first_name, e?.last_name].filter(Boolean).join(' ') || '—';

  const formatTime = (v) => formatTimeEntry(v);

  useEffect(() => {
    workFunctionService.getAll().then((r) => setWorkFunctions(r?.data ?? [])).catch(() => setWorkFunctions([]));
  }, []);

  const loadEntries = async (rangeOverride) => {
    const from = rangeOverride?.dateFrom ?? dateFrom;
    const to = rangeOverride?.dateTo ?? dateTo;
    if (!from || !to) return;
    if (rangeOverride) {
      setDateFrom(rangeOverride.dateFrom);
      setDateTo(rangeOverride.dateTo);
    }
    try {
      setLoading(true);
      setError('');
      const res = await timeService.getAll({ dateFrom: from, dateTo: to, limit: 3000 });
      const list = res.data ?? [];
      setEntries(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load time entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const timecards = useMemo(() => buildTimecards(entries), [entries]);
  const filteredTimecards = useMemo(() => {
    if (!resultsEmployeeFilterId) return timecards;
    return timecards.filter((tc) => String(tc.employeeId) === String(resultsEmployeeFilterId));
  }, [timecards, resultsEmployeeFilterId]);

  const uniqueEmployeesInResults = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const tc of timecards) {
      if (tc.employeeId == null || seen.has(tc.employeeId)) continue;
      seen.add(tc.employeeId);
      list.push({
        id: tc.employeeId,
        employee_number: tc.employeeNumber,
        name: tc.employeeName
      });
    }
    return list.sort((a, b) => String(a.employee_number ?? '').localeCompare(String(b.employee_number ?? '')) || (a.name || '').localeCompare(b.name || ''));
  }, [timecards]);

  /** One row per employee: sums straight/OT across all weeks in the search range (weekly OT rules applied per week, then summed). */
  const payrollBreakdownForRange = useMemo(() => {
    const byEmp = new Map();
    for (const tc of filteredTimecards) {
      const id = tc.employeeId;
      if (id == null) continue;
      if (!byEmp.has(id)) {
        byEmp.set(id, {
          employeeId: id,
          employeeName: tc.employeeName,
          employeeNumber: tc.employeeNumber,
          totalHours: 0,
          straightHours: 0,
          otHours: 0
        });
      }
      const row = byEmp.get(id);
      row.totalHours += tc.totalHours;
      row.straightHours += tc.straightHours;
      row.otHours += tc.otHours;
    }
    const rows = Array.from(byEmp.values()).sort((a, b) =>
      (a.employeeName || '').localeCompare(b.employeeName || '', undefined, { sensitivity: 'base' })
    );
    const totals = rows.reduce(
      (acc, r) => ({
        totalHours: acc.totalHours + r.totalHours,
        straightHours: acc.straightHours + r.straightHours,
        otHours: acc.otHours + r.otHours
      }),
      { totalHours: 0, straightHours: 0, otHours: 0 }
    );
    return { rows, totals };
  }, [filteredTimecards]);

  const payrollBreakdownTitle = useMemo(() => {
    const from = dateFrom && parseLocalDate(dateFrom);
    const to = dateTo && parseLocalDate(dateTo);
    if (!from || !to) return 'Payroll breakdown';
    return `Payroll breakdown for ${format(from, 'MM/dd/yyyy')} to ${format(to, 'MM/dd/yyyy')}`;
  }, [dateFrom, dateTo]);

  const openCreateTimecardDialog = () => {
    setCreateTimecardEmployeeId('');
    setCreateTimecardWeekDate(toYMD(startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_SUNDAY })));
    setCreateTimecardOpen(true);
    setCreateTimecardLoading(true);
    employeeService.getAll({ activeOnly: 'true', limit: 500 }).then((res) => {
      setEmployeesForCreate(Array.isArray(res) ? res : (res?.data ?? []));
    }).catch(() => setEmployeesForCreate([])).finally(() => setCreateTimecardLoading(false));
  };

  const handleCreateTimecardConfirm = () => {
    const emp = employeesForCreate.find((e) => String(e.id) === String(createTimecardEmployeeId));
    if (!emp) return;
    const pickDate = parseLocalDate(createTimecardWeekDate) || new Date(createTimecardWeekDate);
    const weekStart = startOfWeek(pickDate, { weekStartsOn: WEEK_STARTS_SUNDAY });
    const weekEnd = endOfWeek(pickDate, { weekStartsOn: WEEK_STARTS_SUNDAY });
    const tc = {
      employeeId: emp.id,
      employeeName: [emp.first_name, emp.last_name].filter(Boolean).join(' ') || '—',
      employeeNumber: emp.employee_number,
      weekStart,
      weekEnd
    };
    setCreateTimecardOpen(false);
    openWeekView(tc);
  };

  const openWeekView = async (tc) => {
    setWeekViewOpen(true);
    setWeekViewLoading(true);
    setWeekViewData(null);
    try {
      const res = await timeService.getAll({
        employeeId: tc.employeeId,
        dateFrom: toYMD(tc.weekStart),
        dateTo: toYMD(tc.weekEnd),
        limit: 200
      });
      const list = res.data ?? [];
      const entriesByDay = [[], [], [], [], [], [], []];
      for (const entry of list) {
        if (!entry.clock_in) continue;
        const d = parseTimeEntryDate(entry.clock_in) || new Date(entry.clock_in);
        entriesByDay[d.getDay()].push(entry);
      }
      entriesByDay.forEach((dayList) => dayList.sort((a, b) => (parseTimeEntryDate(a.clock_in) || new Date(a.clock_in)).getTime() - (parseTimeEntryDate(b.clock_in) || new Date(b.clock_in)).getTime()));
      setWeekViewData({
        weekStart: tc.weekStart,
        weekEnd: tc.weekEnd,
        employeeId: tc.employeeId,
        employeeName: tc.employeeName,
        employeeNumber: tc.employeeNumber,
        entriesByDay
      });
      setPendingPunches([]);
    } catch (err) {
      setWeekViewData({
        weekStart: tc.weekStart,
        weekEnd: tc.weekEnd,
        employeeId: tc.employeeId,
        employeeName: tc.employeeName,
        employeeNumber: tc.employeeNumber,
        entriesByDay: [[], [], [], [], [], [], []],
        error: err.response?.data?.message || 'Failed to load week'
      });
      setPendingPunches([]);
    } finally {
      setWeekViewLoading(false);
    }
  };

  const openEditPunch = (entry) => {
    setEditRow(entry);
    setEditForm({
      clockIn: toDatetimeLocal(entry.clock_in),
      clockOut: toDatetimeLocal(entry.clock_out),
      hoursWorked: entry.hours_worked != null ? String(entry.hours_worked) : '',
      notes: entry.notes ?? '',
      workOrderNumber: entry.work_order_reference ?? entry.work_order_number ?? '',
      workFunctionId:
        entry.work_function_other && entry.work_function_id == null
          ? WF_OTHER
          : entry.work_function_id != null
            ? String(entry.work_function_id)
            : '',
      workFunctionOther: entry.work_function_other ?? '',
      auditNotes: ''
    });
    setEditDialogOpen(true);
  };

  const refreshWeekViewData = async () => {
    if (!weekViewData?.employeeId) return;
    const res = await timeService.getAll({
      employeeId: weekViewData.employeeId,
      dateFrom: toYMD(weekViewData.weekStart),
      dateTo: toYMD(weekViewData.weekEnd),
      limit: 200
    });
    const list = res.data ?? [];
    const entriesByDay = [[], [], [], [], [], [], []];
      for (const entry of list) {
        if (!entry.clock_in) continue;
        const d = parseTimeEntryDate(entry.clock_in) || new Date(entry.clock_in);
        entriesByDay[d.getDay()].push(entry);
      }
      entriesByDay.forEach((dayList) => dayList.sort((a, b) => (parseTimeEntryDate(a.clock_in) || new Date(a.clock_in)).getTime() - (parseTimeEntryDate(b.clock_in) || new Date(b.clock_in)).getTime()));
    setWeekViewData((prev) => (prev ? { ...prev, entriesByDay } : null));
    await loadEntries();
  };

  const handleSaveEdit = async () => {
    if (!editRow?.id) return;
    if (editForm.workFunctionId === WF_OTHER && !String(editForm.workFunctionOther || '').trim()) {
      setError('Enter a description when Function is Other.');
      return;
    }
    try {
      setEditSaving(true);
      setError('');
      await timeService.update(editRow.id, {
        clockIn: editForm.clockIn ? new Date(editForm.clockIn).toISOString() : undefined,
        clockOut: editForm.clockOut ? new Date(editForm.clockOut).toISOString() : undefined,
        hoursWorked: editForm.hoursWorked ? parseFloat(editForm.hoursWorked) : undefined,
        notes: editForm.notes || undefined,
        workOrderNumber: editForm.workOrderNumber || undefined,
        workFunctionId:
          editForm.workFunctionId === WF_OTHER ? '' : editForm.workFunctionId ? parseInt(editForm.workFunctionId, 10) : '',
        workFunctionOther:
          editForm.workFunctionId === WF_OTHER ? String(editForm.workFunctionOther || '').trim() : '',
        auditNotes: isAdmin ? editForm.auditNotes : undefined
      });
      setEditDialogOpen(false);
      setEditRow(null);
      await refreshWeekViewData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  const addPendingPunch = (dayIndex, afterEntryId = null, afterPendingId = null) => {
    setPendingPunches((prev) => [
      ...prev,
      {
        id: `pending-${Date.now()}-${Math.random()}`,
        dayIndex,
        afterEntryId,
        afterPendingId,
        clockIn: '',
        clockOut: '',
        workOrderNumber: '',
        workFunctionId: '',
        workFunctionOther: ''
      }
    ]);
  };
  const removePendingPunch = (id) => {
    setPendingPunches((prev) => prev.filter((p) => p.id !== id));
  };
  const updatePendingPunch = (id, field, value) => {
    setPendingPunches((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  };

  const startInlineEdit = (entry) => {
    setEditingEntryId(entry.id);
    const dIn = parseTimeEntryDate(entry.clock_in);
    setInlineEditForm({
      clockIn: dIn ? format(dIn, "yyyy-MM-dd'T'HH:mm") : '',
      clockOut: parseTimeEntryDate(entry.clock_out) ? format(parseTimeEntryDate(entry.clock_out), "yyyy-MM-dd'T'HH:mm") : '',
      workOrderNumber: entry.work_order_reference ?? entry.work_order_number ?? '',
      workFunctionId:
        entry.work_function_other && entry.work_function_id == null
          ? WF_OTHER
          : entry.work_function_id != null
            ? String(entry.work_function_id)
            : '',
      workFunctionOther: entry.work_function_other ?? ''
    });
  };
  const cancelInlineEdit = () => {
    setEditingEntryId(null);
  };
  const saveInlineEdit = async () => {
    if (!editingEntryId) return;
    if (inlineEditForm.clockIn && inlineEditForm.clockOut) {
      const inMs = new Date(inlineEditForm.clockIn).getTime();
      const outMs = new Date(inlineEditForm.clockOut).getTime();
      if (outMs < inMs) {
        setError('Clock out must be after clock in. Total hours cannot be negative.');
        return;
      }
    }
    if (inlineEditForm.workFunctionId === WF_OTHER && !String(inlineEditForm.workFunctionOther || '').trim()) {
      setError('Enter a description when Function is Other.');
      return;
    }
    try {
      setError('');
      await timeService.update(editingEntryId, {
        clockIn: inlineEditForm.clockIn ? new Date(inlineEditForm.clockIn).toISOString() : undefined,
        clockOut: inlineEditForm.clockOut ? new Date(inlineEditForm.clockOut).toISOString() : undefined,
        workOrderNumber: (inlineEditForm.workOrderNumber || '').trim() || undefined,
        workFunctionId:
          inlineEditForm.workFunctionId === WF_OTHER
            ? ''
            : inlineEditForm.workFunctionId
              ? parseInt(inlineEditForm.workFunctionId, 10)
              : '',
        workFunctionOther:
          inlineEditForm.workFunctionId === WF_OTHER
            ? String(inlineEditForm.workFunctionOther || '').trim()
            : ''
      });
      setEditingEntryId(null);
      await refreshWeekViewData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };
  const deleteEntry = async (entry) => {
    if (!entry?.id) return;
    try {
      setError('');
      await timeService.delete(entry.id);
      await refreshWeekViewData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const saveNewPunches = async () => {
    if (!weekViewData?.employeeId) return;
    const toSave = pendingPunches.filter((p) => p.clockIn && p.clockOut);
    if (toSave.length === 0) return;
    for (const p of toSave) {
      const d = addDays(weekViewData.weekStart, p.dayIndex);
      const dateStr = toYMD(d);
      const clockInVal = `${dateStr}T${p.clockIn.length === 5 ? p.clockIn : '08:00'}`;
      const clockOutVal = `${dateStr}T${p.clockOut.length === 5 ? p.clockOut : '17:00'}`;
      if (new Date(clockOutVal) <= new Date(clockInVal)) {
        setError('Clock out must be after clock in. Total hours cannot be negative.');
        return;
      }
    }
    try {
      setSavingNewPunches(true);
      setError('');
      for (const p of toSave) {
        if (p.workFunctionId === WF_OTHER && !String(p.workFunctionOther || '').trim()) {
          setError('Enter a description for Function "Other" on new rows that use it.');
          setSavingNewPunches(false);
          return;
        }
        const d = addDays(weekViewData.weekStart, p.dayIndex);
        const dateStr = toYMD(d);
        const clockInVal = `${dateStr}T${p.clockIn.length === 5 ? p.clockIn : '08:00'}`;
        const clockOutVal = `${dateStr}T${p.clockOut.length === 5 ? p.clockOut : '17:00'}`;
        await timeService.create({
          employeeId: weekViewData.employeeId,
          workOrderNumber: (p.workOrderNumber || '').trim() || undefined,
          workFunctionId: p.workFunctionId === WF_OTHER ? '' : p.workFunctionId || undefined,
          workFunctionOther: p.workFunctionId === WF_OTHER ? String(p.workFunctionOther || '').trim() : '',
          clockIn: new Date(clockInVal).toISOString(),
          clockOut: new Date(clockOutVal).toISOString(),
          latitude_in: 0,
          longitude_in: 0,
          device_name: 'Manual entry'
        });
      }
      setPendingPunches([]);
      await refreshWeekViewData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save new punches');
    } finally {
      setSavingNewPunches(false);
    }
  };

  const saveSinglePendingPunch = async (p) => {
    if (!weekViewData?.employeeId || !p.clockIn) return;
    const d = addDays(weekViewData.weekStart, p.dayIndex);
    const dateStr = toYMD(d);
    const clockInVal = `${dateStr}T${String(p.clockIn).length >= 5 ? p.clockIn : '08:00'}`;
    const hasClockOut = p.clockOut && String(p.clockOut).trim() !== '';
    const clockOutVal = hasClockOut ? `${dateStr}T${String(p.clockOut).length >= 5 ? p.clockOut : '17:00'}` : null;
    if (clockOutVal && new Date(clockOutVal) <= new Date(clockInVal)) {
      setError('Clock out must be after clock in. Total hours cannot be negative.');
      return;
    }
    if (p.workFunctionId === WF_OTHER && !String(p.workFunctionOther || '').trim()) {
      setError('Enter a description when Function is Other.');
      return;
    }
    try {
      setSavingPendingId(p.id);
      setError('');
      await timeService.create({
        employeeId: weekViewData.employeeId,
        workOrderNumber: (p.workOrderNumber || '').trim() || undefined,
        workFunctionId: p.workFunctionId === WF_OTHER ? '' : p.workFunctionId || undefined,
        workFunctionOther: p.workFunctionId === WF_OTHER ? String(p.workFunctionOther || '').trim() : '',
        clockIn: new Date(clockInVal).toISOString(),
        clockOut: clockOutVal ? new Date(clockOutVal).toISOString() : undefined,
        latitude_in: 0,
        longitude_in: 0,
        device_name: 'Manual entry'
      });
      removePendingPunch(p.id);
      await refreshWeekViewData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save punch');
    } finally {
      setSavingPendingId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Timecards
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Date range</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
          <Button variant="contained" size="small" startIcon={<Search />} onClick={loadEntries} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </Button>
          {isAdmin && (
            <Button variant="outlined" size="small" startIcon={<Add />} onClick={openCreateTimecardDialog}>
              Create timecard
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 1 }}>
          <Chip
            size="small"
            label="This week"
            onClick={() => {
              const { start, end } = getSundayWeekRangeContaining(new Date());
              void loadEntries({ dateFrom: toYMD(start), dateTo: toYMD(end) });
            }}
            disabled={loading}
            sx={{ cursor: 'pointer' }}
            variant="outlined"
          />
          <Chip
            size="small"
            label="Last week"
            onClick={() => {
              const thisWeekStart = startOfWeek(startOfDay(new Date()), { weekStartsOn: WEEK_STARTS_SUNDAY });
              const { start, end } = getSundayWeekRangeContaining(addDays(thisWeekStart, -1));
              void loadEntries({ dateFrom: toYMD(start), dateTo: toYMD(end) });
            }}
            disabled={loading}
            sx={{ cursor: 'pointer' }}
            variant="outlined"
          />
          <Chip
            size="small"
            label="Before this pay period"
            onClick={() => {
              const { start, end } = getWeekBeforeCurrentPayPeriod();
              void loadEntries({ dateFrom: toYMD(start), dateTo: toYMD(end) });
            }}
            disabled={loading}
            sx={{ cursor: 'pointer' }}
            variant="outlined"
          />
          <Chip
            size="small"
            label="This pay period"
            onClick={() => {
              const { start, end } = getPayPeriodRange(0);
              void loadEntries({ dateFrom: toYMD(start), dateTo: toYMD(end) });
            }}
            disabled={loading}
            sx={{ cursor: 'pointer' }}
            variant="outlined"
          />
          <Chip
            size="small"
            label="Previous pay period"
            onClick={() => {
              const { start, end } = getPayPeriodRange(-1);
              void loadEntries({ dateFrom: toYMD(start), dateTo: toYMD(end) });
            }}
            disabled={loading}
            sx={{ cursor: 'pointer' }}
            variant="outlined"
          />
        </Box>
      </Paper>

      {timecards.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Filter by employee
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Chip
              size="small"
              label="All"
              onClick={() => setResultsEmployeeFilterId(null)}
              color={resultsEmployeeFilterId == null ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            {uniqueEmployeesInResults.map((emp) => (
              <Chip
                key={emp.id}
                size="small"
                label={emp.employee_number ? `${emp.employee_number} ${emp.name}` : emp.name}
                onClick={() => setResultsEmployeeFilterId(emp.id)}
                color={String(resultsEmployeeFilterId) === String(emp.id) ? 'primary' : 'default'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Paper>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Timecards (one per employee per week)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Click a row to open the full week view. {isAdmin && 'Admins can edit punches from the week view.'}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Emp #</TableCell>
                <TableCell>Week (Sun – Sat)</TableCell>
                <TableCell align="right">Total hrs</TableCell>
                <TableCell align="right">Straight</TableCell>
                <TableCell align="right">OT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredTimecards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {entries.length === 0
                        ? 'No time entries in this date range. Adjust dates and Search.'
                        : 'No timecards for the selected employee.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTimecards.map((tc) => (
                  <TableRow
                    key={`${tc.employeeId}-${tc.weekStart.getTime()}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openWeekView(tc)}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{tc.employeeName}</TableCell>
                    <TableCell>{tc.employeeNumber || '—'}</TableCell>
                    <TableCell>
                      {format(tc.weekStart, 'MMM d')} – {format(tc.weekEnd, 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell align="right">{tc.totalHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{tc.straightHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{tc.otHours > 0 ? tc.otHours.toFixed(2) : '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {payrollBreakdownForRange.rows.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {payrollBreakdownTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Straight time vs OT for the date range above. OT uses a 40-hour straight cap per calendar week (Sun–Sat), then totals
            are summed across weeks in this range.
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ maxWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Emp #</TableCell>
                  <TableCell align="right">Total hrs</TableCell>
                  <TableCell align="right">Straight</TableCell>
                  <TableCell align="right">OT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payrollBreakdownForRange.rows.map((row) => (
                  <TableRow key={String(row.employeeId)}>
                    <TableCell>{row.employeeName}</TableCell>
                    <TableCell>{row.employeeNumber || '—'}</TableCell>
                    <TableCell align="right">{row.totalHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{row.straightHours.toFixed(2)}</TableCell>
                    <TableCell align="right">{row.otHours > 0 ? row.otHours.toFixed(2) : '—'}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 600 } }}>
                  <TableCell colSpan={2}>Totals</TableCell>
                  <TableCell align="right">{payrollBreakdownForRange.totals.totalHours.toFixed(2)}</TableCell>
                  <TableCell align="right">{payrollBreakdownForRange.totals.straightHours.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {payrollBreakdownForRange.totals.otHours > 0 ? payrollBreakdownForRange.totals.otHours.toFixed(2) : '—'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create timecard dialog – pick employee + week, then open week view to add entries */}
      <Dialog open={createTimecardOpen} onClose={() => setCreateTimecardOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create timecard</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Open a timecard for an employee and week that has no entries yet. You can then add punches in the week view.
          </Typography>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee</InputLabel>
                <Select
                  label="Employee"
                  value={createTimecardEmployeeId}
                  onChange={(e) => setCreateTimecardEmployeeId(e.target.value)}
                  disabled={createTimecardLoading}
                >
                  <MenuItem value="">Select employee</MenuItem>
                  {employeesForCreate.map((emp) => (
                    <MenuItem key={emp.id} value={String(emp.id)}>
                      {emp.employee_number != null ? `${emp.employee_number} — ` : ''}{[emp.first_name, emp.last_name].filter(Boolean).join(' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Week (pick any day in week)"
                type="date"
                value={createTimecardWeekDate}
                onChange={(e) => setCreateTimecardWeekDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Week: {createTimecardWeekDate && parseLocalDate(createTimecardWeekDate) ? `${format(startOfWeek(parseLocalDate(createTimecardWeekDate), { weekStartsOn: WEEK_STARTS_SUNDAY }), 'MMM d')} – ${format(endOfWeek(parseLocalDate(createTimecardWeekDate), { weekStartsOn: WEEK_STARTS_SUNDAY }), 'MMM d, yyyy')}` : '—'}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTimecardOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTimecardConfirm}
            disabled={!createTimecardEmployeeId}
          >
            Open week view
          </Button>
        </DialogActions>
      </Dialog>

      {/* Week view dialog */}
      <Dialog open={weekViewOpen} onClose={() => { setWeekViewOpen(false); setPendingPunches([]); setEditingEntryId(null); }} maxWidth="xl" fullWidth PaperProps={{ sx: { maxWidth: 1200 } }}>
        <DialogTitle>
          {weekViewData
            ? `Week of ${format(weekViewData.weekStart, 'MMM d, yyyy')} (Sun) – ${format(weekViewData.weekEnd, 'MMM d, yyyy')} (Sat) — ${weekViewData.employeeNumber ? `${weekViewData.employeeNumber} ` : ''}${weekViewData.employeeName}`
            : 'Timecard'}
        </DialogTitle>
        <DialogContent>
          {weekViewLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : weekViewData?.error ? (
            <Alert severity="error">{weekViewData.error}</Alert>
          ) : weekViewData ? (
            <>
              {isAdmin && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Edit inline or use popout for full form. Use + to add a row below. Save new punches when done adding.
                </Typography>
              )}
              <TableContainer>
                <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Day</TableCell>
                      <TableCell>Clock In</TableCell>
                      <TableCell>Clock Out</TableCell>
                      <TableCell align="right">Hours</TableCell>
                      <TableCell>Work Order</TableCell>
                      <TableCell>Function</TableCell>
                      {isAdmin && <TableCell padding="none" width={140}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {DAY_NAMES.map((dayName, dayIndex) => {
                      const dayDate = weekViewData.weekStart ? format(addDays(weekViewData.weekStart, dayIndex), 'MMM d') : '';
                      const dayLabel = dayDate ? `${dayName} ${dayDate}` : dayName;
                      const dayEntries = weekViewData.entriesByDay[dayIndex] || [];
                      const dayPending = pendingPunches.filter((p) => p.dayIndex === dayIndex);
                      const ordered = [];
                      dayEntries.forEach((entry) => {
                        ordered.push({ type: 'entry', data: entry });
                        dayPending.filter((p) => p.afterEntryId === entry.id).forEach((p) => ordered.push({ type: 'pending', data: p }));
                      });
                      dayPending.filter((p) => p.afterEntryId == null && !p.afterPendingId).forEach((p) => ordered.push({ type: 'pending', data: p }));
                      dayPending.filter((p) => p.afterPendingId).forEach((p) => {
                        const i = ordered.findIndex((x) => x.type === 'pending' && x.data.id === p.afterPendingId);
                        if (i >= 0) ordered.splice(i + 1, 0, { type: 'pending', data: p });
                        else ordered.push({ type: 'pending', data: p });
                      });

                      if (ordered.length === 0) {
                        return (
                          <TableRow key={dayName}>
                            <TableCell sx={{ fontWeight: 600 }}>{dayLabel}</TableCell>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary">No punches</Typography>
                            </TableCell>
                            {isAdmin && (
                              <TableCell padding="none">
                                <IconButton size="small" onClick={() => addPendingPunch(dayIndex, null)} title="Add punch" aria-label="Add punch">
                                  <Add fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }
                      return (
                        <React.Fragment key={dayName}>
                          {ordered.map((item, idx) => {
                        const isFirstInDay = idx === 0;
                        if (item.type === 'entry') {
                          const entry = item.data;
                          const isEditing = editingEntryId === entry.id;
                          return (
                            <TableRow key={entry.id} sx={isEditing ? { bgcolor: 'action.hover' } : {}}>
                              <TableCell sx={{ fontWeight: 600, verticalAlign: 'middle' }}>{isFirstInDay ? dayLabel : ''}</TableCell>
                              {isEditing ? (
                                <>
                                  <TableCell>
                                    <TextField size="small" type="datetime-local" value={inlineEditForm.clockIn} onChange={(e) => setInlineEditForm((f) => ({ ...f, clockIn: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth inputProps={{ step: 300 }} />
                                  </TableCell>
                                  <TableCell>
                                    <TextField size="small" type="datetime-local" value={inlineEditForm.clockOut} onChange={(e) => setInlineEditForm((f) => ({ ...f, clockOut: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth inputProps={{ step: 300 }} />
                                  </TableCell>
                                  <TableCell align="right">{entry.hours_worked != null ? Number(entry.hours_worked).toFixed(2) : '—'}</TableCell>
                                  <TableCell>
                                    <TextField size="small" value={inlineEditForm.workOrderNumber} onChange={(e) => setInlineEditForm((f) => ({ ...f, workOrderNumber: e.target.value }))} fullWidth />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <FormControl fullWidth size="small">
                                        <Select
                                          value={inlineEditForm.workFunctionId || ''}
                                          displayEmpty
                                          onChange={(e) =>
                                            setInlineEditForm((f) => ({
                                              ...f,
                                              workFunctionId: e.target.value,
                                              workFunctionOther: e.target.value === WF_OTHER ? f.workFunctionOther : ''
                                            }))
                                          }
                                        >
                                          <MenuItem value="">—</MenuItem>
                                          {workFunctions.map((wf) => (
                                            <MenuItem key={wf.id} value={String(wf.id)}>{wf.label}</MenuItem>
                                          ))}
                                          <MenuItem value={WF_OTHER}>Other…</MenuItem>
                                        </Select>
                                      </FormControl>
                                      {inlineEditForm.workFunctionId === WF_OTHER && (
                                        <TextField
                                          size="small"
                                          placeholder="Describe function"
                                          value={inlineEditForm.workFunctionOther || ''}
                                          onChange={(e) =>
                                            setInlineEditForm((f) => ({ ...f, workFunctionOther: e.target.value }))
                                          }
                                          fullWidth
                                        />
                                      )}
                                    </Box>
                                  </TableCell>
                                  {isAdmin && (
                                    <TableCell padding="none" sx={{ verticalAlign: 'middle' }}>
                                      <IconButton size="small" onClick={saveInlineEdit} title="Save" aria-label="Save"><Check fontSize="small" /></IconButton>
                                      <IconButton size="small" onClick={cancelInlineEdit} title="Cancel" aria-label="Cancel"><Close fontSize="small" /></IconButton>
                                    </TableCell>
                                  )}
                                </>
                              ) : (
                                <>
                                  <TableCell>{formatTime(entry.clock_in)}</TableCell>
                                  <TableCell>{formatTime(entry.clock_out)}</TableCell>
                                  <TableCell align="right">{entry.hours_worked != null ? Number(entry.hours_worked).toFixed(2) : '—'}</TableCell>
                                  <TableCell>{entry.work_order_identifier || entry.work_order_reference || entry.work_order_number || '—'}</TableCell>
                                  <TableCell>{entry.function_label || entry.function_code || '—'}</TableCell>
                                  {isAdmin && (
                                    <TableCell padding="none" sx={{ verticalAlign: 'middle' }}>
                                      <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); startInlineEdit(entry); }} title="Edit inline" aria-label="Edit"><EditIcon fontSize="small" /></IconButton>
                                      <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); addPendingPunch(dayIndex, entry.id); }} title="Add row below" aria-label="Add"><Add fontSize="small" /></IconButton>
                                      <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); openEditPunch(entry); }} title="Popout" aria-label="Popout"><OpenInNew fontSize="small" /></IconButton>
                                      <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); deleteEntry(entry); }} title="Delete" aria-label="Delete"><DeleteIcon fontSize="small" /></IconButton>
                                    </TableCell>
                                  )}
                                </>
                              )}
                            </TableRow>
                          );
                        }
                        const p = item.data;
                        return (
                          <TableRow key={p.id} sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 600, verticalAlign: 'middle' }}>{isFirstInDay ? dayLabel : ''}</TableCell>
                            <TableCell>
                              <TextField size="small" type="time" value={p.clockIn} onChange={(e) => updatePendingPunch(p.id, 'clockIn', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth inputProps={{ step: 300 }} />
                            </TableCell>
                            <TableCell>
                              <TextField size="small" type="time" value={p.clockOut} onChange={(e) => updatePendingPunch(p.id, 'clockOut', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth inputProps={{ step: 300 }} />
                            </TableCell>
                            <TableCell align="right"><Typography variant="body2" color="text.secondary">(new)</Typography></TableCell>
                            <TableCell>
                              <TextField size="small" value={p.workOrderNumber} onChange={(e) => updatePendingPunch(p.id, 'workOrderNumber', e.target.value)} placeholder="Work order" fullWidth />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={p.workFunctionId || ''}
                                    displayEmpty
                                    onChange={(e) => updatePendingPunch(p.id, 'workFunctionId', e.target.value)}
                                  >
                                    <MenuItem value="">—</MenuItem>
                                    {workFunctions.map((wf) => (
                                      <MenuItem key={wf.id} value={String(wf.id)}>{wf.label}</MenuItem>
                                    ))}
                                    <MenuItem value={WF_OTHER}>Other…</MenuItem>
                                  </Select>
                                </FormControl>
                                {p.workFunctionId === WF_OTHER && (
                                  <TextField
                                    size="small"
                                    placeholder="Describe function"
                                    value={p.workFunctionOther || ''}
                                    onChange={(e) => updatePendingPunch(p.id, 'workFunctionOther', e.target.value)}
                                    fullWidth
                                  />
                                )}
                              </Box>
                            </TableCell>
                            {isAdmin && (
                              <TableCell padding="none" sx={{ verticalAlign: 'middle' }}>
                                <IconButton size="small" onClick={() => saveSinglePendingPunch(p)} disabled={!p.clockIn || savingPendingId === p.id} title="Save this punch" aria-label="Save"><Check fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => addPendingPunch(dayIndex, null, p.id)} title="Add row below" aria-label="Add"><Add fontSize="small" /></IconButton>
                                <IconButton size="small" disabled title="Popout (saved entries only)" aria-label="Popout"><OpenInNew fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => removePendingPunch(p.id)} disabled={savingPendingId === p.id} title="Remove" aria-label="Delete"><DeleteIcon fontSize="small" /></IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                        </React.Fragment>
                      );
                    })}
                    {weekViewData.entriesByDay && (
                      <TableRow sx={{ fontWeight: 600, bgcolor: 'action.hover' }}>
                        <TableCell>Total hours</TableCell>
                        <TableCell colSpan={2} />
                        <TableCell align="right">
                          {weekViewData.entriesByDay.flat().reduce((s, e) => s + (parseFloat(e.hours_worked) || 0), 0).toFixed(2)}
                        </TableCell>
                        <TableCell colSpan={2} />
                        {isAdmin && <TableCell />}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setWeekViewOpen(false); setPendingPunches([]); }}>Close</Button>
          {isAdmin && pendingPunches.filter((p) => p.clockIn && p.clockOut).length > 0 && (
            <Button variant="contained" onClick={saveNewPunches} disabled={savingNewPunches}>
              {savingNewPunches ? 'Saving…' : 'Save new punches'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit punch dialog (admin) */}
      <Dialog open={editDialogOpen} onClose={() => !editSaving && (setEditDialogOpen(false), setEditRow(null))} maxWidth="sm" fullWidth>
        <DialogTitle>Edit punch</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Clock In"
                type="datetime-local"
                value={editForm.clockIn}
                onChange={(e) => setEditForm((f) => ({ ...f, clockIn: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Clock Out"
                type="datetime-local"
                value={editForm.clockOut}
                onChange={(e) => setEditForm((f) => ({ ...f, clockOut: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Job # (Work Order)"
                value={editForm.workOrderNumber}
                onChange={(e) => setEditForm((f) => ({ ...f, workOrderNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Function</InputLabel>
                <Select
                  value={editForm.workFunctionId}
                  label="Function"
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      workFunctionId: e.target.value,
                      workFunctionOther: e.target.value === WF_OTHER ? f.workFunctionOther : ''
                    }))
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {workFunctions.map((wf) => (
                    <MenuItem key={wf.id} value={String(wf.id)}>{wf.label}</MenuItem>
                  ))}
                  <MenuItem value={WF_OTHER}>Other…</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editForm.workFunctionId === WF_OTHER && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Function (other)"
                  value={editForm.workFunctionOther}
                  onChange={(e) => setEditForm((f) => ({ ...f, workFunctionOther: e.target.value }))}
                  placeholder="e.g. one-off task"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Hours"
                type="number"
                inputProps={{ step: 0.25, min: 0 }}
                value={editForm.hoursWorked}
                onChange={(e) => setEditForm((f) => ({ ...f, hoursWorked: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                rows={2}
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
            {isAdmin && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Audit note (reason for edit)"
                  value={editForm.auditNotes}
                  onChange={(e) => setEditForm((f) => ({ ...f, auditNotes: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setEditRow(null); }} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Timecards;
